import { useState, useEffect, useRef } from 'react';

const SECTION_ICONS = { workout: '⚡', meditation: '🧘', flexibility: '🤸', generic: '⏱', custom: '✦', breathing: '🌬️' };
const SECTION_COLORS = {
  workout: { bg: '#ff4d1c', light: '#fff1ee' },
  meditation: { bg: '#3d5af1', light: '#eef0ff' },
  flexibility: { bg: '#9b59b6', light: '#f5eeff' },
  generic: { bg: '#555e6e', light: '#f0f1f3' },
  custom: { bg: '#00b87a', light: '#e6fff5' },
  breathing: { bg: '#00b4d8', light: '#e0f7fc' },
};

const DIAL_SIZE = 220;
const DIAL_RADIUS = 90;
const DIAL_STROKE = 14;
const CIRCUMFERENCE = 2 * Math.PI * DIAL_RADIUS;

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

let sharedAudioCtx = null;
const getAudioCtx = () => {
  if (!sharedAudioCtx) sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return sharedAudioCtx;
};
const resumeAudio = () => { try { getAudioCtx().resume(); } catch { } };

const playBell = () => {
  try {
    const ctx = getAudioCtx();
    ctx.resume().then(() => {
      // Deep gong: low fundamental with harmonics and long decay
      [[90, 0.6], [180, 0.3], [270, 0.12], [360, 0.06]].forEach(([freq, vol]) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = 'sine'; o.frequency.value = freq;
        g.gain.setValueAtTime(0, ctx.currentTime);
        g.gain.linearRampToValueAtTime(vol, ctx.currentTime + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 5);
        o.start(ctx.currentTime); o.stop(ctx.currentTime + 5.1);
      });
    });
  } catch { }
};

// ── Preset Timer ──────────────────────────────────────────────────────────────
// Subroutine shape: { name, duration (seconds | null for open-ended), delay (seconds), interval? (seconds, for open-ended) }
// State machine per subroutine: delay phase → active phase → next subroutine
function PresetTimer({ preset, color, onMinuteReached }) {
  const { subroutines } = preset;

  const [ts, setTs] = useState({ phaseIdx: 0, subPhase: 'active', elapsed: 0 });
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  // Always reflects latest state — the interval reads from this to avoid stale closures
  const tsRef = useRef(ts);
  tsRef.current = ts;

  const totalElapsedRef = useRef(0);
  const minuteReachedRef = useRef(false);

  useEffect(() => {
    if (!running || done) return;
    const id = setInterval(() => {
      const { phaseIdx, subPhase, elapsed } = tsRef.current;
      const sub = subroutines[phaseIdx];
      const next = elapsed + 1;

      totalElapsedRef.current += 1;
      if (!minuteReachedRef.current && totalElapsedRef.current >= 60) {
        minuteReachedRef.current = true;
        onMinuteReached?.();
      }

      if (subPhase === 'delay') {
        if (next >= sub.delay) {
          playBell();
          setTs({ phaseIdx, subPhase: 'active', elapsed: 0 });
        } else {
          setTs({ phaseIdx, subPhase, elapsed: next });
        }
      } else {
        if (sub.duration && next >= sub.duration) {
          playBell();
          const nextIdx = phaseIdx + 1;
          if (nextIdx < subroutines.length) {
            const nextSub = subroutines[nextIdx];
            const nextSubPhase = nextSub.delay > 0 ? 'delay' : 'active';
            setTs({ phaseIdx: nextIdx, subPhase: nextSubPhase, elapsed: 0 });
          } else {
            setDone(true);
            setRunning(false);
          }
        } else {
          if (!sub.duration && sub.interval && next % sub.interval === 0) playBell();
          setTs({ phaseIdx, subPhase, elapsed: next });
        }
      }
    }, 1000);
    return () => clearInterval(id);
  }, [running, done, subroutines, onMinuteReached]);

  const toggle = () => {
    resumeAudio();
    if (running) setRunning(false);
    else if (!done) setRunning(true);
  };

  const reset = () => {
    setRunning(false);
    setTs({ phaseIdx: 0, subPhase: 'active', elapsed: 0 });
    setDone(false);
    totalElapsedRef.current = 0;
    minuteReachedRef.current = false;
  };

  const { phaseIdx, subPhase, elapsed } = ts;
  const sub = subroutines[phaseIdx];
  const timeDisplay = done ? 'Done!'
    : subPhase === 'delay' ? fmt(sub.delay - elapsed)
      : sub.duration ? fmt(Math.max(0, sub.duration - elapsed))
        : fmt(elapsed);

  const phaseLabel = done ? 'Complete'
    : subPhase === 'delay' ? 'Rest'
      : sub.name;

  return (
    <div>
      {/* Progress bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {subroutines.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 4, transition: 'all 0.3s',
            background: i <= phaseIdx ? color.bg : '#e0e0e0',
            opacity: i < phaseIdx ? 0.3 : 1
          }} />
        ))}
      </div>

      {subPhase === 'delay' ? (
        /* ── Dual timer: rest countdown + frozen next subroutine ── */
        <div style={{ marginBottom: 14 }}>
          {/* Rest countdown — active */}
          <div style={{ textAlign: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: color.bg, letterSpacing: 1, marginBottom: 6 }}>REST</div>
            <div style={{ ...s.display, background: color.light, border: `2px solid ${color.bg}` }}>
              <span style={{ fontSize: 58, fontFamily: "'Bebas Neue'", letterSpacing: 4, color: color.bg }}>
                {fmt(sub.delay - elapsed)}
              </span>
            </div>
          </div>
          {/* Next subroutine — frozen/paused */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#bbb', letterSpacing: 1, marginBottom: 6 }}>UP NEXT · {sub.name}</div>
            <div style={{ ...s.display, background: '#f5f5f5', border: '2px solid #e0e0e0' }}>
              <span style={{ fontSize: 58, fontFamily: "'Bebas Neue'", letterSpacing: 4, color: '#ccc' }}>
                {sub.duration ? fmt(sub.duration) : '∞'}
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* ── Single timer: active phase ── */
        <>
          <div style={{ textAlign: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: done ? '#aaa' : '#111' }}>{phaseLabel}</div>
            <div style={{ fontSize: 12, color: '#bbb', marginTop: 3 }}>
              {!done && !sub.duration && `Open-ended · bell every ${sub.interval / 60} min`}
              {!done && sub.duration && `${sub.duration / 60} min`}
            </div>
          </div>
          <div style={{ ...s.display, background: color.light, border: `2px solid ${color.bg}`, marginBottom: 14 }}>
            <span style={{ fontSize: 58, fontFamily: "'Bebas Neue'", letterSpacing: 4, color: done ? '#aaa' : color.bg }}>
              {timeDisplay}
            </span>
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button onClick={toggle} disabled={done}
          style={{ ...s.btn, flex: 2, background: done ? '#ccc' : running ? '#333' : color.bg }}>
          {running ? '⏸ Pause' : elapsed > 0 && !done ? '▶ Resume' : '▶ Start'}
        </button>
        <button onClick={reset} style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }}>Reset</button>
      </div>

      {/* Subroutine list */}
      <div style={{ background: '#fafafa', borderRadius: 12, padding: '10px 14px' }}>
        <p style={{ ...s.label, marginBottom: 8 }}>Sequence</p>
        {subroutines.map((sr, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
            borderBottom: i < subroutines.length - 1 ? '1px solid #f0f0f0' : 'none'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: 14, fontWeight: i === phaseIdx ? 700 : 400,
                color: i === phaseIdx ? color.bg : i < phaseIdx ? '#aaa' : '#333'
              }}>
                {sr.name}
              </div>
              <div style={{ fontSize: 12, color: '#bbb' }}>
                {sr.delay > 0 && `${sr.delay}s delay · `}
                {sr.duration ? fmt(sr.duration) : `Open · bell every ${sr.interval / 60}m`}
              </div>
            </div>
            {i < phaseIdx && <span style={{ color: color.bg, fontWeight: 700 }}>✓</span>}
            {i === phaseIdx && !done && (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                color: subPhase === 'delay' ? '#aaa' : color.bg,
                background: subPhase === 'delay' ? '#f0f0f0' : color.light
              }}>
                {subPhase === 'delay' ? 'Prepare' : running ? 'Active' : 'Ready'}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Generic Timer ─────────────────────────────────────────────────────────────
function GenericTimer({ section, color, onMinuteReached }) {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [intervals, setIntervals] = useState([]);
  const [newInterval, setNewInterval] = useState('');
  const [alerts, setAlerts] = useState([]);
  const ref = useRef(null);
  const minuteReachedRef = useRef(false);

  const toggle = () => { if (running) { clearInterval(ref.current); setRunning(false); } else setRunning(true); };
  const reset = () => { clearInterval(ref.current); setRunning(false); setElapsed(0); setAlerts([]); };

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setElapsed((e) => {
        const next = e + 1;
        if (!minuteReachedRef.current && next >= 60) {
          minuteReachedRef.current = true;
          onMinuteReached?.();
        }
        intervals.forEach((iv) => {
          if (next === iv * 60) {
            const msg = `⏰ ${iv} min${iv > 1 ? 's' : ''} – time to switch!`;
            setAlerts((a) => [...a, msg]);
            if ('Notification' in window && Notification.permission === 'granted')
              new Notification(section.name, { body: msg });
            playBell();
          }
        });
        return next;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running, intervals, section.name, onMinuteReached]);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission();
  }, []);

  const addInterval = () => {
    const v = parseInt(newInterval);
    if (v > 0 && !intervals.includes(v)) { setIntervals((p) => [...p, v].sort((a, b) => a - b)); setNewInterval(''); }
  };

  return (
    <>
      <div style={{ ...s.display, background: color.light, border: `2px solid ${color.bg}` }}>
        <span style={{ fontSize: 60, fontFamily: "'Bebas Neue'", color: color.bg, letterSpacing: 4 }}>{fmt(elapsed)}</span>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 0' }}>
        <button onClick={toggle} style={{ ...s.btn, background: running ? '#333' : color.bg, flex: 2 }}>
          {running ? '⏸ Pause' : elapsed > 0 ? '▶ Resume' : '▶ Start'}
        </button>
        <button onClick={reset} style={{ ...s.btn, background: '#eee', color: '#333', flex: 1 }}>Reset</button>
      </div>
      <div style={s.intervalBox}>
        <p style={s.label}>Interval Alerts (minutes)</p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {[1, 2, 3, 5, 10, 15, 20, 30].map((v) => (
            <button key={v} onClick={() => !intervals.includes(v) && setIntervals((p) => [...p, v].sort((a, b) => a - b))}
              style={{ ...s.pill, background: intervals.includes(v) ? color.bg : '#eee', color: intervals.includes(v) ? '#fff' : '#333' }}>
              {v}m
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={newInterval} onChange={(e) => setNewInterval(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addInterval()}
            placeholder="Custom mins" type="number" style={{ ...s.input, flex: 1 }} />
          <button onClick={addInterval} style={{ ...s.btn, background: color.bg, padding: '8px 16px', fontSize: 14 }}>Add</button>
        </div>
        {intervals.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {intervals.map((v) => (
              <span key={v} onClick={() => setIntervals((p) => p.filter((x) => x !== v))}
                style={{ ...s.pill, background: color.light, color: color.bg, border: `1px solid ${color.bg}`, marginRight: 4, cursor: 'pointer' }}>
                {v}m ✕
              </span>
            ))}
          </div>
        )}
      </div>
      {alerts.length > 0 && (
        <div style={s.alertBox}>
          {alerts.slice(-3).map((a, i) => <div key={i} style={{ fontSize: 14, color: '#c73200', fontWeight: 600, padding: '3px 0' }}>{a}</div>)}
        </div>
      )}
    </>
  );
}

// ── Breathing Timer ───────────────────────────────────────────────────────────
function BreathingTimer({ config, color, onMinuteReached }) {
  const { inhale, hold, exhale, hold2, totalDuration } = config;
  const cycleLen = inhale + (hold || 0) + exhale + (hold2 || 0);
  const totalCycles = Math.ceil(totalDuration / cycleLen);

  const [running, setRunning] = useState(false);
  const [totalElapsed, setTotalElapsed] = useState(0);

  const runStartRef = useRef(null);
  const baseElapsedRef = useRef(0);
  const totalElapsedRef = useRef(0);
  const minuteReachedRef = useRef(false);
  const prevPhaseRef = useRef(null);
  const prevTotalRef = useRef(-1);
  const onMinuteReachedRef = useRef(onMinuteReached);
  onMinuteReachedRef.current = onMinuteReached;

  // Preload audio buffers via Web Audio API
  const buffersRef = useRef({ inhale: null, exhale: null });
  const naturalDurRef = useRef({ inhale: 3, exhale: 3 });

  useEffect(() => {
    let active = true;
    const loadBuf = async (url) => {
      const res = await fetch(url);
      const ab = await res.arrayBuffer();
      return getAudioCtx().decodeAudioData(ab);
    };
    Promise.all([loadBuf('/inhale.mp3'), loadBuf('/exhale.mp3')])
      .then(([iBuf, eBuf]) => {
        if (!active) return;
        buffersRef.current = { inhale: iBuf, exhale: eBuf };
        naturalDurRef.current = { inhale: iBuf.duration, exhale: eBuf.duration };
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const playPhaseSound = (phase, phaseDuration) => {
    if (phase === 'hold' || phase === 'hold2' || phase === 'done') return;
    try {
      const key = phase === 'inhale' ? 'inhale' : 'exhale';
      const buffer = buffersRef.current[key];
      if (!buffer) return;
      const ctx = getAudioCtx();
      const rate = Math.max(0.25, Math.min(4.0, naturalDurRef.current[key] / phaseDuration));
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.playbackRate.value = rate;
      src.connect(ctx.destination);
      src.start(ctx.currentTime);
    } catch {}
  };

  const getPhase = (elapsed) => {
    if (elapsed >= totalDuration) return { phase: 'done', countdown: 0, cycleNum: totalCycles, phaseDuration: 1 };
    const pos = elapsed % cycleLen;
    const cycleNum = Math.floor(elapsed / cycleLen) + 1;
    if (pos < inhale) return { phase: 'inhale', countdown: inhale - pos, cycleNum, phaseDuration: inhale };
    const holdEnd = inhale + (hold || 0);
    if (hold && pos < holdEnd) return { phase: 'hold', countdown: holdEnd - pos, cycleNum, phaseDuration: hold };
    const exhaleEnd = holdEnd + exhale;
    if (pos < exhaleEnd) return { phase: 'exhale', countdown: exhaleEnd - pos, cycleNum, phaseDuration: exhale };
    return { phase: 'hold2', countdown: cycleLen - pos, cycleNum, phaseDuration: hold2 || 0 };
  };

  const getFillFraction = (elapsed) => {
    if (elapsed >= totalDuration) return 1;
    const pos = elapsed % cycleLen;
    if (pos < inhale) return pos / inhale;
    const holdEnd = inhale + (hold || 0);
    if (hold && pos < holdEnd) return 1;
    const exhaleEnd = holdEnd + exhale;
    if (pos < exhaleEnd) return 1 - (pos - holdEnd) / exhale;
    return 0;
  };

  const advanceRef = useRef(null);
  advanceRef.current = () => {
    if (runStartRef.current === null) return;
    const wallElapsed = (Date.now() - runStartRef.current) / 1000;
    const newTotal = Math.min(baseElapsedRef.current + wallElapsed, totalDuration);
    if (newTotal - prevTotalRef.current < 0.05) return;
    prevTotalRef.current = newTotal;

    if (!minuteReachedRef.current && newTotal >= 60) {
      minuteReachedRef.current = true;
      onMinuteReachedRef.current?.();
    }

    const { phase, phaseDuration } = getPhase(newTotal);
    if (prevPhaseRef.current !== phase) playPhaseSound(phase, phaseDuration);
    prevPhaseRef.current = phase;

    totalElapsedRef.current = newTotal;
    setTotalElapsed(newTotal);
    if (newTotal >= totalDuration) setRunning(false);
  };

  useEffect(() => {
    if (!running) return;
    runStartRef.current = Date.now();
    baseElapsedRef.current = totalElapsedRef.current;
    prevPhaseRef.current = null;
    prevTotalRef.current = totalElapsedRef.current - 1;
    const id = setInterval(() => advanceRef.current(), 100);
    return () => { clearInterval(id); runStartRef.current = null; };
  }, [running]);

  const done = totalElapsed >= totalDuration;
  const { phase, countdown, cycleNum } = getPhase(totalElapsed);
  const fillFraction = getFillFraction(totalElapsed);
  const strokeDashoffset = CIRCUMFERENCE * (1 - fillFraction);

  const phaseLabel = done ? '' : phase === 'inhale' ? 'INHALE' : phase === 'exhale' ? 'EXHALE' : 'HOLD';
  const phaseColor = done
    ? { bg: '#aaa', light: '#f5f5f5' }
    : phase === 'hold' ? { bg: '#9b59b6', light: '#f5eeff' }
    : phase === 'exhale' ? { bg: '#00b87a', light: '#e6fff5' }
    : phase === 'hold2' ? { bg: '#f39c12', light: '#fef9e7' }
    : color;

  const toggle = () => {
    resumeAudio();
    if (running) setRunning(false);
    else if (!done) setRunning(true);
  };

  const reset = () => {
    setRunning(false);
    setTotalElapsed(0);
    totalElapsedRef.current = 0;
    minuteReachedRef.current = false;
    prevPhaseRef.current = null;
    prevTotalRef.current = -1;
  };

  return (
    <div>
      {/* Cycle count */}
      <div style={{ textAlign: 'center', fontSize: 13, color: '#aaa', marginBottom: 12 }}>
        {done ? 'Session Complete' : `Cycle ${cycleNum} of ${totalCycles}`}
      </div>

      {/* SVG dial */}
      <div style={{ position: 'relative', width: DIAL_SIZE, margin: '0 auto 16px' }}>
        <svg width={DIAL_SIZE} height={DIAL_SIZE} style={{ display: 'block' }}>
          <circle cx={DIAL_SIZE / 2} cy={DIAL_SIZE / 2} r={DIAL_RADIUS}
            stroke="#eee" strokeWidth={DIAL_STROKE} fill="none" />
          <circle cx={DIAL_SIZE / 2} cy={DIAL_SIZE / 2} r={DIAL_RADIUS}
            stroke={phaseColor.bg} strokeWidth={DIAL_STROKE} fill="none"
            strokeDasharray={CIRCUMFERENCE} strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'stroke-dashoffset 0.5s linear, stroke 0.3s ease' }}
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: phaseColor.bg, letterSpacing: 2, marginBottom: 4 }}>{phaseLabel}</div>
          <div style={{ fontSize: 52, fontWeight: 700, color: phaseColor.bg, lineHeight: 1 }}>
            {done ? '✓' : Math.ceil(countdown)}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button onClick={toggle} disabled={done}
          style={{ ...s.btn, flex: 2, background: done ? '#ccc' : running ? '#333' : color.bg }}>
          {running ? '⏸ Pause' : totalElapsed > 0 && !done ? '▶ Resume' : '▶ Start'}
        </button>
        <button onClick={reset} style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }}>Reset</button>
      </div>

      {/* Pattern summary */}
      <div style={{ background: '#fafafa', borderRadius: 12, padding: 14 }}>
        <p style={{ ...s.label, marginBottom: 8 }}>Pattern</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, textAlign: 'center', background: color.light, borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 11, color: color.bg, fontWeight: 600, marginBottom: 4 }}>INHALE</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: color.bg }}>{inhale}</div>
          </div>
          {hold > 0 && (
            <div style={{ flex: 1, textAlign: 'center', background: '#f5eeff', borderRadius: 10, padding: 10 }}>
              <div style={{ fontSize: 11, color: '#9b59b6', fontWeight: 600, marginBottom: 4 }}>HOLD</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#9b59b6' }}>{hold}</div>
            </div>
          )}
          <div style={{ flex: 1, textAlign: 'center', background: '#e6fff5', borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 11, color: '#00b87a', fontWeight: 600, marginBottom: 4 }}>EXHALE</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#00b87a' }}>{exhale}</div>
          </div>
          {hold2 > 0 && (
            <div style={{ flex: 1, textAlign: 'center', background: '#fef9e7', borderRadius: 10, padding: 10 }}>
              <div style={{ fontSize: 11, color: '#f39c12', fontWeight: 600, marginBottom: 4 }}>HOLD 2</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#f39c12' }}>{hold2}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
export default function TimerModal({ section, preset, onMinuteReached, onClose }) {
  const color = SECTION_COLORS[section.type] || SECTION_COLORS.custom;

  return (
    <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...s.modal, borderTop: `5px solid ${color.bg}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 30 }}>{SECTION_ICONS[section.type]}</span>
              <div>
                <h2 style={{ fontSize: 19, fontWeight: 700, color: '#111', margin: 0 }}>{section.name}</h2>
                {preset && <p style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{preset.name}</p>}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>

        {preset?.subroutines?.[0]?.breathing
          ? <BreathingTimer key={preset.name} config={preset.subroutines[0]} color={color} onMinuteReached={onMinuteReached} />
          : preset
          ? <PresetTimer key={preset.name} preset={preset} color={color} onMinuteReached={onMinuteReached} />
          : <GenericTimer section={section} color={color} onMinuteReached={onMinuteReached} />
        }
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', backdropFilter: 'blur(3px)' },
  modal: { background: '#fff', width: '100%', maxWidth: 480, borderRadius: '20px 20px 0 0', padding: '28px 24px', maxHeight: '90vh', overflowY: 'auto' },
  closeBtn: { background: '#f0f0f0', border: 'none', width: 32, height: 32, borderRadius: 8, fontSize: 14, color: '#555', cursor: 'pointer' },
  display: { borderRadius: 14, padding: '20px 0', textAlign: 'center' },
  btn: { border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 15, fontWeight: 700, color: '#fff', cursor: 'pointer' },
  intervalBox: { background: '#fafafa', borderRadius: 12, padding: 14, marginBottom: 12 },
  label: { fontSize: 12, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5 },
  pill: { border: 'none', borderRadius: 20, padding: '5px 11px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  input: { border: '1.5px solid #e5e5e5', borderRadius: 9, padding: '8px 12px', fontSize: 14, color: '#111', background: '#fafafa' },
  alertBox: { background: '#fff8f0', border: '1px solid #ffddcc', borderRadius: 10, padding: 12 },
};